/**
 * A text message cut into the words and the `https://` addresses in it, so the bubble can draw the
 * addresses as links. Only `https` — a plain `http://` or any other scheme stays text. Pure.
 */

import { httpsLinkOf } from '~/shared/lib/httpsLink';

export type TextSegment =
  | { readonly kind: 'text'; readonly text: string }
  | { readonly kind: 'link'; readonly text: string; readonly href: string; readonly host: string };

const CANDIDATE = /(?<![\p{L}\p{N}_])https:\/\/[^\s<>"]+/giu;
// Sentence punctuation after an address belongs to the sentence, not the address.
const TRAILING = /[.,;:!?'"»”’…]$/u;
const CLOSERS: Readonly<Record<string, string>> = { ')': '(', ']': '[', '}': '{' };

function count(text: string, char: string): number {
  return text.split(char).length - 1;
}

/** The address without what the sentence put after it; a closing bracket stays when it is balanced. */
function trimTail(candidate: string): string {
  let out = candidate;
  for (;;) {
    const last = out.slice(-1);
    const opener = CLOSERS[last];
    if (TRAILING.test(last)) {
      out = out.slice(0, -1);
    } else if (opener !== undefined && count(out, last) > count(out, opener)) {
      out = out.slice(0, -1);
    } else {
      return out;
    }
  }
}

export function splitLinks(text: string): TextSegment[] {
  const segments: TextSegment[] = [];
  let cursor = 0;
  const pushText = (end: number) => {
    if (end > cursor) segments.push({ kind: 'text', text: text.slice(cursor, end) });
  };
  for (const match of text.matchAll(CANDIDATE)) {
    const start = match.index;
    const address = trimTail(match[0]);
    const link = httpsLinkOf(address);
    if (link === null) continue;
    pushText(start);
    segments.push({ kind: 'link', text: address, href: link.href, host: link.host });
    cursor = start + address.length;
  }
  pushText(text.length);
  return segments;
}
